<template>
  <div class="p-3 pb-16 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
    <div class="container mx-auto max-w-2xl py-8">
      <!-- Main Card -->
      <div class="bg-gray-900 rounded-xl shadow-2xl p-6">
        <h1 class="text-2xl font-bold text-white mb-6 text-center">การจัดการเซิร์ฟเวอร์</h1>

        <!-- Player Management Section -->
        <div class="mb-8">
          <h2 class="text-xl font-semibold text-blue-400 mb-4">จัดการจำนวนผู้เล่น</h2>
          <div class="bg-gray-800 p-4 rounded-lg">
            <!-- Active Players Display -->
            <div class="mb-4">
              <p class="text-white">
                ผู้เล่นที่กำลังใช้งาน: <span class="font-bold text-blue-400">{{ activePlayer }}</span> คน
              </p>
            </div>
            <!-- Max Players Input -->
            <div class="flex items-center space-x-4 mb-4">
              <input
                v-model.number="maxPlayer"
                type="number"
                min="0"
                class="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="กำหนดจำนวนผู้เล่นสูงสุด"
                :disabled="isUpdatingMax"
              />
              <button
                @click="updateMaxPlayer"
                class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
                :disabled="isUpdatingMax || maxPlayer < 0"
              >
                {{ isUpdatingMax ? 'กำลังบันทึก...' : 'บันทึก' }}
              </button>
            </div>
            <!-- Character Creation Toggle -->
            <div class="flex items-center justify-between mb-4">
              <p class="text-white">
                การสร้างตัวละคร: <span class="font-bold" :class="canCreateCharacter ? 'text-green-400' : 'text-red-400'">{{ canCreateCharacter ? 'เปิด' : 'ปิด' }}</span>
              </p>
              <button
                @click="toggleCharacterCreation"
                class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition duration-300"
                :disabled="isTogglingCharacterCreation"
              >
                {{ isTogglingCharacterCreation ? 'กำลังอัปเดต...' : canCreateCharacter ? 'ปิดการสร้าง' : 'เปิดการสร้าง' }}
              </button>
            </div>
            <!-- PvP Toggle -->
            <div class="flex items-center justify-between mb-4">
              <p class="text-white">
                การต่อสู้ PvP: <span class="font-bold" :class="isPvpEnabled ? 'text-green-400' : 'text-red-400'">{{ isPvpEnabled ? 'เปิด' : 'ปิด' }}</span>
              </p>
              <button
                @click="togglePvp"
                class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-300"
                :disabled="isTogglingPvp"
              >
                {{ isTogglingPvp ? 'กำลังอัปเดต...' : isPvpEnabled ? 'ปิด PvP' : 'เปิด PvP' }}
              </button>
            </div>
            <!-- Admin Online Toggle -->
            <div class="flex items-center justify-between mb-4">
              <p class="text-white">
                สถานะแอดมินออนไลน์: <span class="font-bold" :class="isAdminOnline ? 'text-green-400' : 'text-red-400'">{{ isAdminOnline ? 'ออนไลน์' : 'ออฟไลน์' }}</span>
              </p>
              <button
                @click="toggleAdminOnline"
                class="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 transition duration-300"
                :disabled="isTogglingAdminOnline"
              >
                {{ isTogglingAdminOnline ? 'กำลังอัปเดต...' : isAdminOnline ? 'ตั้งเป็นออฟไลน์' : 'ตั้งเป็นออนไลน์' }}
              </button>
            </div>
            <!-- Max Players and Toggle Messages -->
            <transition name="fade">
              <div v-if="maxMessage" class="mt-4 text-center">
                <p :class="maxMessageType === 'success' ? 'text-green-400' : 'text-red-400'">{{ maxMessage }}</p>
              </div>
            </transition>
            <transition name="fade">
              <div v-if="toggleMessage" class="mt-4 text-center">
                <p :class="toggleMessageType === 'success' ? 'text-green-400' : 'text-red-400'">{{ toggleMessage }}</p>
              </div>
            </transition>
            <transition name="fade">
              <div v-if="pvpMessage" class="mt-4 text-center">
                <p :class="pvpMessageType === 'success' ? 'text-green-400' : 'text-red-400'">{{ pvpMessage }}</p>
              </div>
            </transition>
            <transition name="fade">
              <div v-if="adminOnlineMessage" class="mt-4 text-center">
                <p :class="adminOnlineMessageType === 'success' ? 'text-green-400' : 'text-red-400'">{{ adminOnlineMessage }}</p>
              </div>
            </transition>
          </div>
        </div>

        <!-- Item Spawn Section -->
        <div class="mb-8">
          <h2 class="text-xl font-semibold text-green-400 mb-4">เสกไอเทมให้ผู้เล่น</h2>
          <div class="bg-gray-800 p-4 rounded-lg">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label class="text-white text-sm font-semibold mb-2 block">UID ผู้เล่น</label>
                <input
                  v-model.trim="spawnUid"
                  type="text"
                  placeholder="กรอก UID ผู้เล่น"
                  class="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition duration-300"
                  :disabled="isSpawningItem"
                />
              </div>
              <div>
                <label class="text-white text-sm font-semibold mb-2 block">ชื่อไอเทม</label>
                <input
                  v-model.trim="spawnItemName"
                  type="text"
                  placeholder="กรอกชื่อไอเทม (เช่น Health Potion)"
                  class="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition duration-300"
                  :disabled="isSpawningItem"
                />
              </div>
            </div>
            <button
              @click="spawnItemToBag"
              class="w-full px-6 py-3 bg-green-600 text-white font-semibold rounded-lg shadow-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition duration-300"
              :disabled="isSpawningItem || !spawnUid || !spawnItemName"
            >
              {{ isSpawningItem ? 'กำลังเสกไอเทม...' : 'เสกไอเทม' }}
            </button>
            <!-- Success/Error Message -->
            <transition name="fade">
              <div v-if="spawnMessage" class="mt-4 text-center">
                <p :class="spawnMessageType === 'success' ? 'text-green-400' : 'text-red-400'">{{ spawnMessage }}</p>
              </div>
            </transition>
          </div>
        </div>

        <!-- Add Villager to Camp Section -->
        <div class="mb-8">
          <h2 class="text-xl font-semibold text-green-400 mb-4">เพิ่มชาวบ้านให้แคมป์</h2>
          <div class="bg-gray-800 p-4 rounded-lg">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label class="text-white text-sm font-semibold mb-2 block">UID ผู้เล่น</label>
                <input
                  v-model.trim="villagerUid"
                  type="text"
                  placeholder="กรอก UID ผู้เล่น"
                  class="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition duration-300"
                  :disabled="isAddingVillager"
                />
              </div>
              <div>
                <label class="text-white text-sm font-semibold mb-2 block">ชื่อชาวบ้าน</label>
                <input
                  v-model.trim="villagerName"
                  type="text"
                  placeholder="กรอกชื่อชาวบ้าน (เช่น ชาวบ้าน A)"
                  class="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition duration-300"
                  :disabled="isAddingVillager"
                />
              </div>
            </div>
            <button
              @click="addVillagerToCamp"
              class="w-full px-6 py-3 bg-green-600 text-white font-semibold rounded-lg shadow-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition duration-300"
              :disabled="isAddingVillager || !villagerUid || !villagerName"
            >
              {{ isAddingVillager ? 'กำลังเพิ่มชาวบ้าน...' : 'เพิ่มชาวบ้าน' }}
            </button>
            <!-- Success/Error Message -->
            <transition name="fade">
              <div v-if="villagerMessage" class="mt-4 text-center">
                <p :class="villagerMessageType === 'success' ? 'text-green-400' : 'text-red-400'">{{ villagerMessage }}</p>
              </div>
            </transition>
          </div>
        </div>

        <!-- Add Cash to All Players Section -->
        <div class="mb-8">
          <h2 class="text-xl font-semibold text-yellow-400 mb-4">เพิ่มแคชให้ผู้เล่นทุกคน</h2>
          <div class="bg-gray-800 p-4 rounded-lg">
            <div class="flex items-center space-x-4 mb-4">
              <input
                v-model.number="addCashAmount"
                type="number"
                min="0"
                class="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                placeholder="กรอกจำนวนแคชที่ต้องการเพิ่ม"
                :disabled="isAddingCash"
              />
              <button
                @click="addCashToAllPlayers"
                class="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 transition duration-300"
                :disabled="isAddingCash || addCashAmount <= 0"
              >
                {{ isAddingCash ? 'กำลังเพิ่มแคช...' : 'เพิ่มแคช' }}
              </button>
            </div>
            <!-- Success/Error Message -->
            <transition name="fade">
              <div v-if="cashMessage" class="mt-4 text-center">
                <p :class="cashMessageType === 'success' ? 'text-green-400' : 'text-red-400'">{{ cashMessage }}</p>
              </div>
            </transition>
          </div>
        </div>

        <!-- Add Cash to Specific Player Section -->
        <div class="mb-8">
          <h2 class="text-xl font-semibold text-yellow-400 mb-4">เพิ่มแคชให้ผู้เล่นตาม UID</h2>
          <div class="bg-gray-800 p-4 rounded-lg">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label class="text-white text-sm font-semibold mb-2 block">UID ผู้เล่น</label>
                <input
                  v-model.trim="specificUid"
                  type="text"
                  placeholder="กรอก UID ผู้เล่น"
                  class="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 transition duration-300"
                  :disabled="isAddingCashToSpecific"
                />
              </div>
              <div>
                <label class="text-white text-sm font-semibold mb-2 block">จำนวนแคช</label>
                <input
                  v-model.number="specificCashAmount"
                  type="number"
                  min="0"
                  placeholder="กรอกจำนวนแคช"
                  class="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 transition duration-300"
                  :disabled="isAddingCashToSpecific"
                />
              </div>
            </div>
            <button
              @click="addCashToSpecificPlayer"
              class="w-full px-6 py-3 bg-yellow-600 text-white font-semibold rounded-lg shadow-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 transition duration-300"
              :disabled="isAddingCashToSpecific || !specificUid || specificCashAmount <= 0"
            >
              {{ isAddingCashToSpecific ? 'กำลังเพิ่มแคช...' : 'เพิ่มแคช' }}
            </button>
            <!-- Success/Error Message -->
            <transition name="fade">
              <div v-if="specificCashMessage" class="mt-4 text-center">
                <p :class="specificCashMessageType === 'success' ? 'text-green-400' : 'text-red-400'">{{ specificCashMessage }}</p>
              </div>
            </transition>
          </div>
        </div>

        <!-- Mysterious Box Button -->
        <div class="mb-8">
          <h2 class="text-xl font-semibold text-purple-400 mb-4">จัดการคลังปริศนา</h2>
          <div class="bg-gray-800 p-4 rounded-lg">
            <button
              @click="showMysteriousBoxModal = true"
              class="w-full px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg shadow-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition duration-300"
            >
              เปิดจัดการคลังปริศนา
            </button>
          </div>
        </div>

        <!-- Weekly Reward Tiers Building 1 -->
        <div class="mb-8">
          <h2 class="text-xl font-semibold text-indigo-400 mb-4">จัดการรางวัลประจำสัปดาห์ตึก1</h2>
          <div class="bg-gray-800 p-4 rounded-lg">
            <button
              @click="showWeeklyRewardModalBuilding1 = true"
              class="w-full px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg shadow-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-300"
            >
              เปิดจัดการรางวัลประจำสัปดาห์ตึก1
            </button>
          </div>
        </div>

        <!-- Weekly Reward Tiers Building 2 -->
        <div class="mb-8">
          <h2 class="text-xl font-semibold text-indigo-400 mb-4">จัดการรางวัลประจำสัปดาห์ตึก2</h2>
          <div class="bg-gray-800 p-4 rounded-lg">
            <button
              @click="showWeeklyRewardModalBuilding2 = true"
              class="w-full px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg shadow-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-300"
            >
              เปิดจัดการรางวัลประจำสัปดาห์ตึก2
            </button>
          </div>
        </div>

        <!-- Weekly Reward Tiers Occupation -->
        <div class="mb-8">
          <h2 class="text-xl font-semibold text-indigo-400 mb-4">จัดการรางวัลประจำสัปดาห์ระบบผู้ยึดครอง</h2>
          <div class="bg-gray-800 p-4 rounded-lg">
            <button
              @click="showWeeklyRewardModalOccupation = true"
              class="w-full px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg shadow-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-300"
            >
              เปิดจัดการรางวัลประจำสัปดาห์ระบบผู้ยึดครอง
            </button>
          </div>
        </div>

        <!-- Banned Sale Items -->
        <div class="mb-8">
          <h2 class="text-xl font-semibold text-red-400 mb-4">จัดการไอเทมห้ามขาย</h2>
          <div class="bg-gray-800 p-4 rounded-lg">
            <button
              @click="showBannedItemsModal = true"
              class="w-full px-6 py-3 bg-red-600 text-white font-semibold rounded-lg shadow-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition duration-300"
            >
              เปิดจัดการไอเทมห้ามขาย
            </button>
          </div>
        </div>

        <!-- Killer Robots Active -->
        <div class="mb-8">
          <h2 class="text-xl font-semibold text-red-400 mb-4">จัดการหุ่นยนต์สังหาร</h2>
          <div class="bg-gray-800 p-4 rounded-lg">
            <div class="flex items-center space-x-4 mb-4">
              <select v-model="newKillerActive" class="px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option :value="true">เปิด active (true)</option>
                <option :value="false">ปิด active (false)</option>
              </select>
              <button
                @click="updateAllKillerActive"
                class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-300"
                :disabled="isUpdatingKiller"
              >
                {{ isUpdatingKiller ? 'กำลังอัปเดต...' : 'อัปเดตทั้งหมด' }}
              </button>
            </div>
            <transition name="fade">
              <div v-if="killerMessage" class="mt-4 text-center">
                <p :class="killerMessageType === 'success' ? 'text-green-400' : 'text-red-400'">{{ killerMessage }}</p>
              </div>
            </transition>
          </div>
        </div>

        <!-- Topup Bonus Management -->
        <div class="mb-8">
          <h2 class="text-xl font-semibold text-pink-400 mb-4">จัดการโบนัสเติมเงิน</h2>
          <div class="bg-gray-800 p-4 rounded-lg">
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div>
                <label class="text-white text-sm font-semibold mb-2 block">เปอร์เซ็นต์โบนัส</label>
                <input
                  v-model.number="bonusPercentage"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="เช่น 20 สำหรับ 20%"
                  class="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 transition duration-300"
                />
              </div>
              <div>
                <label class="text-white text-sm font-semibold mb-2 block">เวลาหมดอายุ</label>
                <input
                  v-model="bonusEndDate"
                  type="datetime-local"
                  class="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 transition duration-300"
                />
              </div>
              <div>
                <label class="text-white text-sm font-semibold mb-2 block">ข้อความอธิบาย</label>
                <input
                  v-model="bonusMessage"
                  type="text"
                  placeholder="เช่น วันนี้มีโบนัสเพราะเหตุการณ์พิเศษ"
                  class="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 transition duration-300"
                />
              </div>
            </div>
            <button
              @click="updateBonus"
              class="w-full px-6 py-3 bg-pink-600 text-white font-semibold rounded-lg shadow-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 transition duration-300"
              :disabled="isUpdatingBonus || bonusPercentage < 0"
            >
              {{ isUpdatingBonus ? 'กำลังอัปเดต...' : 'อัปเดตโบนัส' }}
            </button>
            <!-- Success/Error Message -->
            <transition name="fade">
              <div v-if="bonusMessageStatus" class="mt-4 text-center">
                <p :class="bonusMessageType === 'success' ? 'text-green-400' : 'text-red-400'">{{ bonusMessageStatus }}</p>
              </div>
            </transition>
          </div>
        </div>

        <!-- Auto Approve Topup -->
        <div class="mb-8">
          <h2 class="text-xl font-semibold text-cyan-400 mb-4">จัดการการอนุมัติเติมเงินอัตโนมัติ</h2>
          <div class="bg-gray-800 p-4 rounded-lg">
            <div class="flex items-center justify-between mb-4">
              <p class="text-white">
                การอนุมัติอัตโนมัติ: <span class="font-bold" :class="isAutoApproveTopup ? 'text-green-400' : 'text-red-400'">{{ isAutoApproveTopup ? 'ใช่' : 'ไม่ใช่' }}</span>
              </p>
              <button
                @click="toggleAutoApproveTopup"
                class="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 transition duration-300"
                :disabled="isTogglingAutoApproveTopup"
              >
                {{ isTogglingAutoApproveTopup ? 'กำลังอัปเดต...' : isAutoApproveTopup ? 'ปิดอัตโนมัติ' : 'เปิดอัตโนมัติ' }}
              </button>
            </div>
            <transition name="fade">
              <div v-if="autoApproveMessage" class="mt-4 text-center">
                <p :class="autoApproveMessageType === 'success' ? 'text-green-400' : 'text-red-400'">{{ autoApproveMessage }}</p>
              </div>
            </transition>
          </div>
        </div>

        <!-- New Section: Banned Chat Users -->
        <div class="mb-8">
          <h2 class="text-xl font-semibold text-orange-400 mb-4">จัดการผู้เล่นที่ห้ามแชท</h2>
          <div class="bg-gray-800 p-4 rounded-lg">
            <div class="space-y-4 mb-4">
              <div v-for="(uid, index) in bannedChatUsers" :key="index" class="p-4 bg-gray-700 rounded-lg animate-fade-in">
                <div class="grid grid-cols-12 items-center gap-3">
                  <div class="col-span-10">
                    <p class="text-white">UID: {{ uid }}</p>
                  </div>
                  <div class="col-span-2">
                    <button
                      @click="removeBannedChatUser(index)"
                      class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-300"
                    >
                      ลบ
                    </button>
                  </div>
                </div>
              </div>
              <div class="flex gap-3 mb-3">
                <input
                  v-model="newBannedChatUser"
                  type="text"
                  placeholder="กรอก UID ผู้เล่นที่ต้องการห้ามแชท"
                  class="flex-1 px-4 py-2 bg-gray-600 rounded-lg border border-gray-500 focus:ring-2 focus:ring-orange-500 focus:outline-none text-white"
                />
                <button
                  @click="addBannedChatUser"
                  class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-300"
                >
                  + เพิ่ม
                </button>
              </div>
            </div>
            <button
              @click="saveBannedChatUsers"
              class="w-full px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg shadow-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 transition duration-300"
              :disabled="isSavingBannedChatUsers"
            >
              {{ isSavingBannedChatUsers ? 'กำลังบันทึก...' : 'บันทึก' }}
            </button>
            <!-- Success/Error Message -->
            <transition name="fade">
              <div v-if="bannedChatUsersMessage" class="mt-4 text-center">
                <p :class="bannedChatUsersMessageType === 'success' ? 'text-green-400' : 'text-red-400'">{{ bannedChatUsersMessage }}</p>
              </div>
            </transition>
          </div>
        </div>

        <!-- NPC Reclaim Outposts Section -->
        <div class="mb-8">
          <h2 class="text-xl font-semibold text-purple-400 mb-4">NPC ทวงคืนเอาท์โพสต์</h2>
          <div class="bg-gray-800 p-4 rounded-lg">
            <p class="text-sm text-gray-300 mb-3">อัตโนมัติทุกวันเวลา 12:00 และ 18:00 น. (เวลาไทย) เลเวล NPC 6–10 ครบ 14 สถานที่</p>
            <button
              @click="showNpcReclaimModal = true"
              class="w-full px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg shadow-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition duration-300"
            >
              NPC ทวงคืนเอาท์โพสต์
            </button>
          </div>
        </div>

        <!-- Unlock Four Dice Requirements Section -->
        <div class="mb-8">
          <h2 class="text-xl font-semibold text-cyan-400 mb-4">เลือกไอเท็มที่ใช้สำหรับ 4 ลูกเต๋า</h2>
          <div class="bg-gray-800 p-4 rounded-lg">
            <button
              @click="showUnlockFourDiceModal = true"
              class="w-full px-6 py-3 bg-cyan-600 text-white font-semibold rounded-lg shadow-md hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 transition duration-300"
            >
              เปิดจัดการไอเท็มสำหรับ 4 ลูกเต๋า
            </button>
          </div>
        </div>

        <!-- Unlock Five Dice Requirements Section -->
        <div class="mb-8">
          <h2 class="text-xl font-semibold text-cyan-400 mb-4">เลือกไอเท็มที่ใช้สำหรับ 5 ลูกเต๋า</h2>
          <div class="bg-gray-800 p-4 rounded-lg">
            <button
              @click="showUnlockFiveDiceModal = true"
              class="w-full px-6 py-3 bg-cyan-600 text-white font-semibold rounded-lg shadow-md hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 transition duration-300"
            >
              เปิดจัดการไอเท็มสำหรับ 5 ลูกเต๋า
            </button>
          </div>
        </div>

        <!-- Unlock Six Dice Requirements Section -->
        <div class="mb-8">
          <h2 class="text-xl font-semibold text-cyan-400 mb-4">เลือกไอเท็มที่ใช้สำหรับ 6 ลูกเต๋า</h2>
          <div class="bg-gray-800 p-4 rounded-lg">
            <button
              @click="showUnlockSixDiceModal = true"
              class="w-full px-6 py-3 bg-cyan-600 text-white font-semibold rounded-lg shadow-md hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 transition duration-300"
            >
              เปิดจัดการไอเท็มสำหรับ 6 ลูกเต๋า
            </button>
          </div>
        </div>

      </div>
    </div>

    <!-- Mysterious Box Modal -->
    <div v-if="showMysteriousBoxModal" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75">
      <!-- Overlay -->
      <div class="absolute inset-0" @click="closeMysteriousBoxModal"></div>
      <!-- Modal Container -->
      <div class="relative w-full max-w-lg sm:max-w-2xl h-[80vh] overflow-hidden bg-gray-800 rounded-xl shadow-2xl text-white flex flex-col">
        <div class="flex justify-between items-center p-6 border-b border-gray-700">
          <h2 class="text-xl font-semibold text-purple-300">จัดการคลังปริศนา</h2>
          <button @click="closeMysteriousBoxModal" class="text-gray-400 hover:text-white focus:outline-none" aria-label="ปิด">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <!-- Modal Content -->
        <div class="flex-1 overflow-y-auto p-6 scroll-smooth scrollbar-thin scrollbar-thumb-purple-500 scrollbar-track-gray-700">
          <div class="space-y-4 h-60 overflow-y-auto">
            <div v-for="(reward, index) in rewards" :key="index" class="p-4 bg-gray-700 rounded-lg animate-fade-in">
              <div class="grid grid-cols-12 items-center gap-3">
                <div class="col-span-6 sm:col-span-7">
                  <input
                    v-model="reward.itemName"
                    type="text"
                    placeholder="ชื่อไอเทม (เช่น กระสุน)"
                    class="w-full px-4 py-2 bg-gray-600 rounded-lg border border-gray-500 focus:ring-2 focus:ring-purple-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-4 sm:col-span-3">
                  <input
                    v-model.number="reward.chance"
                    type="number"
                    min="0"
                    max="100"
                    placeholder="%"
                    class="w-full px-4 py-2 bg-gray-600 rounded-lg border border-gray-500 focus:ring-2 focus:ring-purple-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-2">
                  <button
                    @click="removeReward(index)"
                    class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-300"
                  >
                    ลบ
                  </button>
                </div>
              </div>
            </div>
            <div class="flex gap-3 mb-3">
              <button
                @click="addReward"
                class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-300"
              >
                + เพิ่มไอเทม
              </button>
              <button
                @click="normalizeChances"
                class="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white transition duration-300"
              >
                คำนวณโอกาส
              </button>
            </div>
            <div v-if="totalChance > 0" class="text-sm text-gray-400 mt-2">
              <p>โอกาสการสุ่มไอเทม (รวม: {{ totalChance.toFixed(2) }}%):</p>
              <p v-if="remainingChance > 0" class="text-yellow-500">เหลือ: {{ remainingChance.toFixed(2) }}%</p>
              <p v-else-if="remainingChance < 0" class="text-red-500">เกิน: {{ Math.abs(remainingChance).toFixed(2) }}%</p>
              <ul class="list-disc list-inside">
                <li v-for="(reward, index) in rewards" :key="index">
                  {{ reward.itemName || 'ยังไม่ระบุ' }}: {{ reward.chance || 0 }}%
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="flex justify-between gap-3 p-6 border-t border-gray-700 bg-gray-800">
          <button
            @click="closeMysteriousBoxModal"
            class="px-4 py-2 bg-gray-600 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-white transition duration-300"
          >
            ยกเลิก
          </button>
          <button
            @click="saveRewards"
            class="px-4 py-2 bg-purple-600 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 text-white transition duration-300"
            :disabled="isSavingRewards || totalChance > 100"
          >
            {{ isSavingRewards ? 'กำลังบันทึก...' : 'บันทึก' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Weekly Reward Modal Building 1 -->
    <div v-if="showWeeklyRewardModalBuilding1" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75">
      <!-- Overlay -->
      <div class="absolute inset-0" @click="closeWeeklyRewardModalBuilding1"></div>
      <!-- Modal Container -->
      <div class="relative w-full max-w-lg sm:max-w-2xl h-[80vh] overflow-hidden bg-gray-800 rounded-xl shadow-2xl text-white flex flex-col">
        <!-- Header -->
        <div class="flex justify-between items-center p-6 border-b border-gray-700">
          <h2 class="text-xl font-semibold text-indigo-300">จัดการรางวัลประจำสัปดาห์ตึก1</h2>
          <button @click="closeWeeklyRewardModalBuilding1" class="text-gray-400 hover:text-white focus:outline-none" aria-label="ปิด">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <!-- Modal Content -->
        <div class="flex-1 overflow-y-auto p-6 scroll-smooth scrollbar-thin scrollbar-thumb-indigo-500 scrollbar-track-gray-700">
          <div class="space-y-4 h-60 overflow-y-auto">
            <div v-for="(tier, index) in weeklyTiersBuilding1" :key="index" class="p-4 bg-gray-700 rounded-lg animate-fade-in">
              <div class="grid grid-cols-12 items-center gap-3">
                <div class="col-span-3">
                  <input
                    v-model.number="tier.min"
                    type="number"
                    min="1"
                    placeholder="อันดับเริ่มต้น"
                    class="w-full px-4 py-2 bg-gray-600 rounded-lg border border-gray-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-3">
                  <input
                    v-model.number="tier.max"
                    type="number"
                    min="1"
                    placeholder="อันดับสิ้นสุด"
                    class="w-full px-4 py-2 bg-gray-600 rounded-lg border border-gray-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-3">
                  <input
                    v-model="tier.itemName"
                    type="text"
                    placeholder="ชื่อไอเทม"
                    class="w-full px-4 py-2 bg-gray-600 rounded-lg border border-gray-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-2">
                  <input
                    v-model.number="tier.quantity"
                    type="number"
                    min="0"
                    placeholder="จำนวน"
                    class="w-full px-4 py-2 bg-gray-600 rounded-lg border border-gray-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-1">
                  <button
                    @click="removeTierBuilding1(index)"
                    class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-300"
                  >
                    ลบ
                  </button>
                </div>
              </div>
            </div>
            <div class="flex gap-3 mb-3">
              <button
                @click="addTierBuilding1"
                class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-300"
              >
                + เพิ่มช่วงอันดับ
              </button>
            </div>
          </div>
        </div>
        <!-- Fixed Action Buttons -->
        <div class="flex justify-between gap-3 p-6 border-t border-gray-700 bg-gray-800">
          <button
            @click="closeWeeklyRewardModalBuilding1"
            class="px-4 py-2 bg-gray-600 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-white transition duration-300"
          >
            ยกเลิก
          </button>
          <button
            @click="saveWeeklyTiersBuilding1"
            class="px-4 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white transition duration-300"
            :disabled="isSavingWeeklyTiersBuilding1"
          >
            {{ isSavingWeeklyTiersBuilding1 ? 'กำลังบันทึก...' : 'บันทึก' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Weekly Reward Modal Building 2 -->
    <div v-if="showWeeklyRewardModalBuilding2" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75">
      <!-- Overlay -->
      <div class="absolute inset-0" @click="closeWeeklyRewardModalBuilding2"></div>
      <!-- Modal Container -->
      <div class="relative w-full max-w-lg sm:max-w-2xl h-[80vh] overflow-hidden bg-gray-800 rounded-xl shadow-2xl text-white flex flex-col">
        <!-- Header -->
        <div class="flex justify-between items-center p-6 border-b border-gray-700">
          <h2 class="text-xl font-semibold text-indigo-300">จัดการรางวัลประจำสัปดาห์ตึก2</h2>
          <button @click="closeWeeklyRewardModalBuilding2" class="text-gray-400 hover:text-white focus:outline-none" aria-label="ปิด">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <!-- Modal Content -->
        <div class="flex-1 overflow-y-auto p-6 scroll-smooth scrollbar-thin scrollbar-thumb-indigo-500 scrollbar-track-gray-700">
          <div class="space-y-4 h-60 overflow-y-auto">
            <div v-for="(tier, index) in weeklyTiersBuilding2" :key="index" class="p-4 bg-gray-700 rounded-lg animate-fade-in">
              <div class="grid grid-cols-12 items-center gap-3">
                <div class="col-span-3">
                  <input
                    v-model.number="tier.min"
                    type="number"
                    min="1"
                    placeholder="อันดับเริ่มต้น"
                    class="w-full px-4 py-2 bg-gray-600 rounded-lg border border-gray-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-3">
                  <input
                    v-model.number="tier.max"
                    type="number"
                    min="1"
                    placeholder="อันดับสิ้นสุด"
                    class="w-full px-4 py-2 bg-gray-600 rounded-lg border border-gray-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-3">
                  <input
                    v-model="tier.itemName"
                    type="text"
                    placeholder="ชื่อไอเทม"
                    class="w-full px-4 py-2 bg-gray-600 rounded-lg border border-gray-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-2">
                  <input
                    v-model.number="tier.quantity"
                    type="number"
                    min="0"
                    placeholder="จำนวน"
                    class="w-full px-4 py-2 bg-gray-600 rounded-lg border border-gray-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-1">
                  <button
                    @click="removeTierBuilding2(index)"
                    class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-300"
                  >
                    ลบ
                  </button>
                </div>
              </div>
            </div>
            <div class="flex gap-3 mb-3">
              <button
                @click="addTierBuilding2"
                class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-300"
              >
                + เพิ่มช่วงอันดับ
              </button>
            </div>
          </div>
        </div>
        <!-- Fixed Action Buttons -->
        <div class="flex justify-between gap-3 p-6 border-t border-gray-700 bg-gray-800">
          <button
            @click="closeWeeklyRewardModalBuilding2"
            class="px-4 py-2 bg-gray-600 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-white transition duration-300"
          >
            ยกเลิก
          </button>
          <button
            @click="saveWeeklyTiersBuilding2"
            class="px-4 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white transition duration-300"
            :disabled="isSavingWeeklyTiersBuilding2"
          >
            {{ isSavingWeeklyTiersBuilding2 ? 'กำลังบันทึก...' : 'บันทึก' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Weekly Reward Modal Occupation -->
    <div v-if="showWeeklyRewardModalOccupation" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75">
      <!-- Overlay -->
      <div class="absolute inset-0" @click="closeWeeklyRewardModalOccupation"></div>
      <!-- Modal Container -->
      <div class="relative w-full max-w-lg sm:max-w-2xl h-[80vh] overflow-hidden bg-gray-800 rounded-xl shadow-2xl text-white flex flex-col">
        <!-- Header -->
        <div class="flex justify-between items-center p-6 border-b border-gray-700">
          <h2 class="text-xl font-semibold text-indigo-300">จัดการรางวัลประจำสัปดาห์ระบบผู้ยึดครอง</h2>
          <button @click="closeWeeklyRewardModalOccupation" class="text-gray-400 hover:text-white focus:outline-none" aria-label="ปิด">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <!-- Modal Content -->
        <div class="flex-1 overflow-y-auto p-6 scroll-smooth scrollbar-thin scrollbar-thumb-indigo-500 scrollbar-track-gray-700">
          <div class="space-y-4 h-60 overflow-y-auto">
            <div v-for="(tier, index) in weeklyTiersOccupation" :key="index" class="p-4 bg-gray-700 rounded-lg animate-fade-in">
              <div class="grid grid-cols-12 items-center gap-3">
                <div class="col-span-3">
                  <input
                    v-model.number="tier.min"
                    type="number"
                    min="1"
                    placeholder="อันดับเริ่มต้น"
                    class="w-full px-4 py-2 bg-gray-600 rounded-lg border border-gray-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-3">
                  <input
                    v-model.number="tier.max"
                    type="number"
                    min="1"
                    placeholder="อันดับสิ้นสุด"
                    class="w-full px-4 py-2 bg-gray-600 rounded-lg border border-gray-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-3">
                  <input
                    v-model="tier.itemName"
                    type="text"
                    placeholder="ชื่อไอเทม"
                    class="w-full px-4 py-2 bg-gray-600 rounded-lg border border-gray-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-2">
                  <input
                    v-model.number="tier.quantity"
                    type="number"
                    min="0"
                    placeholder="จำนวน"
                    class="w-full px-4 py-2 bg-gray-600 rounded-lg border border-gray-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-1">
                  <button
                    @click="removeTierOccupation(index)"
                    class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-300"
                  >
                    ลบ
                  </button>
                </div>
              </div>
            </div>
            <div class="flex gap-3 mb-3">
              <button
                @click="addTierOccupation"
                class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-300"
              >
                + เพิ่มช่วงอันดับ
              </button>
            </div>
          </div>
        </div>
        <!-- Fixed Action Buttons -->
        <div class="flex justify-between gap-3 p-6 border-t border-gray-700 bg-gray-800">
          <button
            @click="closeWeeklyRewardModalOccupation"
            class="px-4 py-2 bg-gray-600 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-white transition duration-300"
          >
            ยกเลิก
          </button>
          <button
            @click="saveWeeklyTiersOccupation"
            class="px-4 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white transition duration-300"
            :disabled="isSavingWeeklyTiersOccupation"
          >
            {{ isSavingWeeklyTiersOccupation ? 'กำลังบันทึก...' : 'บันทึก' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Banned Items Modal -->
    <div v-if="showBannedItemsModal" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75">
      <!-- Overlay -->
      <div class="absolute inset-0" @click="closeBannedItemsModal"></div>
      <!-- Modal Container -->
      <div class="relative w-full max-w-lg sm:max-w-2xl h-[80vh] overflow-hidden bg-gray-800 rounded-xl shadow-2xl text-white flex flex-col">
        <div class="flex justify-between items-center p-6 border-b border-gray-700">
          <h2 class="text-xl font-semibold text-red-300">จัดการไอเทมห้ามขาย</h2>
          <button @click="closeBannedItemsModal" class="text-gray-400 hover:text-white focus:outline-none" aria-label="ปิด">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <!-- Modal Content -->
        <div class="flex-1 overflow-y-auto p-6 scroll-smooth scrollbar-thin scrollbar-thumb-red-500 scrollbar-track-gray-700">
          <div class="space-y-4 h-60 overflow-y-auto">
            <div v-for="(item, index) in bannedItems" :key="index" class="p-4 bg-gray-700 rounded-lg animate-fade-in">
              <div class="grid grid-cols-12 items-center gap-3">
                <div class="col-span-10">
                  <p class="text-white">{{ item }}</p>
                </div>
                <div class="col-span-2">
                  <button
                    @click="removeBannedItem(index)"
                    class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-300"
                  >
                    ลบ
                  </button>
                </div>
              </div>
            </div>
            <div class="flex gap-3 mb-3">
              <input
                v-model="newBannedItem"
                type="text"
                placeholder="ชื่อไอเทมที่ห้ามขาย"
                class="flex-1 px-4 py-2 bg-gray-600 rounded-lg border border-gray-500 focus:ring-2 focus:ring-red-500 focus:outline-none text-white"
              />
              <button
                @click="addBannedItem"
                class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-300"
              >
                + เพิ่ม
              </button>
            </div>
          </div>
        </div>
        <!-- Fixed Action Buttons -->
        <div class="flex justify-between gap-3 p-6 border-t border-gray-700 bg-gray-800">
          <button
            @click="closeBannedItemsModal"
            class="px-4 py-2 bg-gray-600 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-white transition duration-300"
          >
            ยกเลิก
          </button>
          <button
            @click="saveBannedItems"
            class="px-4 py-2 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-300"
            :disabled="isSavingBannedItems"
          >
            {{ isSavingBannedItems ? 'กำลังบันทึก...' : 'บันทึก' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Npc Reclaim Modal -->
    <NpcReclaimModal v-if="showNpcReclaimModal" @close="showNpcReclaimModal = false" />

    <!-- Unlock Four Dice Modal -->
    <UnlockFourDiceModal v-if="showUnlockFourDiceModal" @close="showUnlockFourDiceModal = false" />

    <!-- Unlock Five Dice Modal -->
    <UnlockFiveDiceModal v-if="showUnlockFiveDiceModal" @close="showUnlockFiveDiceModal = false" />

    <!-- Unlock Six Dice Modal -->
    <UnlockSixDiceModal v-if="showUnlockSixDiceModal" @close="showUnlockSixDiceModal = false" />

  </div>
</template>

<script>
import { projectFirestore, projectFunctions } from '@/firebase/config';
import { ref, computed, onUnmounted, onMounted } from 'vue';
import { Timestamp } from 'firebase/firestore';
import gameVillagersData from '@/data/gameVillagers.json';
import NpcReclaimModal from '@/components/admin/NpcReclaimModal.vue';
import UnlockFourDiceModal from '@/components/UnlockFourDiceModal.vue';
import UnlockFiveDiceModal from '@/components/UnlockFiveDiceModal.vue';
import UnlockSixDiceModal from '@/components/UnlockSixDiceModal.vue'; // Import the new modal

export default {
  components: {
    NpcReclaimModal,
    UnlockFourDiceModal,
    UnlockFiveDiceModal,
    UnlockSixDiceModal
  },
  setup() {
    const activePlayer = ref(0);
    const maxPlayer = ref(0);
    const isUpdatingMax = ref(false);
    const maxMessage = ref('');
    const maxMessageType = ref('');
    const canCreateCharacter = ref(false);
    const isTogglingCharacterCreation = ref(false);
    const toggleMessage = ref('');
    const toggleMessageType = ref('');
    const showMysteriousBoxModal = ref(false);
    const rewards = ref([{ itemName: '', chance: 0 }]);
    const isSavingRewards = ref(false);
    const rewardMessage = ref('');
    const rewardMessageType = ref('');
    const spawnUid = ref('');
    const spawnItemName = ref('');
    const isSpawningItem = ref(false);
    const spawnMessage = ref('');
    const spawnMessageType = ref('');
    const addCashAmount = ref(0);
    const isAddingCash = ref(false);
    const cashMessage = ref('');
    const cashMessageType = ref('');
    const specificUid = ref('');
    const specificCashAmount = ref(0);
    const isAddingCashToSpecific = ref(false);
    const specificCashMessage = ref('');
    const specificCashMessageType = ref('');
    const isPvpEnabled = ref(false);
    const isTogglingPvp = ref(false);
    const pvpMessage = ref('');
    const pvpMessageType = ref('');
    const isAdminOnline = ref(false);
    const isTogglingAdminOnline = ref(false);
    const adminOnlineMessage = ref('');
    const adminOnlineMessageType = ref('');
    // New variables for weekly reward building 1
    const showWeeklyRewardModalBuilding1 = ref(false);
    const weeklyTiersBuilding1 = ref([]);
    const isSavingWeeklyTiersBuilding1 = ref(false);
    // New variables for weekly reward building 2
    const showWeeklyRewardModalBuilding2 = ref(false);
    const weeklyTiersBuilding2 = ref([]);
    const isSavingWeeklyTiersBuilding2 = ref(false);
    // New variables for weekly reward occupation
    const showWeeklyRewardModalOccupation = ref(false);
    const weeklyTiersOccupation = ref([]);
    const isSavingWeeklyTiersOccupation = ref(false);
    // New variables for banned items
    const showBannedItemsModal = ref(false);
    const bannedItems = ref([]);
    const newBannedItem = ref('');
    const isSavingBannedItems = ref(false);
    // New variables for killer robots
    const newKillerActive = ref(true);
    const isUpdatingKiller = ref(false);
    const killerMessage = ref('');
    const killerMessageType = ref('');
    // New variables for topup bonus
    const bonusPercentage = ref(0);
    const bonusEndDate = ref('');
    const bonusMessage = ref('');
    const isUpdatingBonus = ref(false);
    const bonusMessageStatus = ref('');
    const bonusMessageType = ref('');
    // New variables for auto approve topup
    const isAutoApproveTopup = ref(false);
    const isTogglingAutoApproveTopup = ref(false);
    const autoApproveMessage = ref('');
    const autoApproveMessageType = ref('');
    // New variables for banned chat users
    const bannedChatUsers = ref([]);
    const newBannedChatUser = ref('');
    const isSavingBannedChatUsers = ref(false);
    const bannedChatUsersMessage = ref('');
    const bannedChatUsersMessageType = ref('');
    // New variables for add villager to camp
    const villagerUid = ref('');
    const villagerName = ref('');
    const isAddingVillager = ref(false);
    const villagerMessage = ref('');
    const villagerMessageType = ref('');
    // New variable for NPC reclaim modal
    const showNpcReclaimModal = ref(false);
    // New variable for Unlock Four Dice modal
    const showUnlockFourDiceModal = ref(false);
    // New variable for Unlock Five Dice modal
    const showUnlockFiveDiceModal = ref(false);
    // New variable for Unlock Six Dice modal
    const showUnlockSixDiceModal = ref(false);

    // Computed properties
    const totalChance = computed(() => rewards.value.reduce((sum, r) => sum + (r.chance || 0), 0));
    const remainingChance = computed(() => 100 - totalChance.value);

    // Real-time listeners
    const serverDocRef = projectFirestore.collection('server').doc('setting');
    const adminDocRef = projectFirestore.collection('server').doc('admin');
    const mysteriousBoxRef = projectFirestore.collection('server').doc('mysteriousBox');
    const weeklyRewardsBuilding1Ref = projectFirestore.collection('server').doc('weeklyRewardsBuilding1');
    const weeklyRewardsBuilding2Ref = projectFirestore.collection('server').doc('weeklyRewardsBuilding2');
    const weeklyRewardsOccupationRef = projectFirestore.collection('server').doc('weeklyRewardsOccupation');
    const bannedItemsRef = projectFirestore.collection('server').doc('bannedSaleItems');
    const bonusRef = projectFirestore.collection('server').doc('bonus');
    const bannedChatUsersRef = projectFirestore.collection('server').doc('bannedChatUsers');

    const unsubscribeSettings = serverDocRef.onSnapshot(
      (doc) => {
        if (doc.exists) {
          activePlayer.value = doc.data().activePlayer || 0;
          maxPlayer.value = doc.data().maxPlayer || 0;
          canCreateCharacter.value = doc.data().canCreateCharacter || false;
          isPvpEnabled.value = doc.data().pvp || false;
          isAutoApproveTopup.value = doc.data().autoApproveTopup || false;
        } else {
          console.warn('Server settings document does not exist');
        }
      },
      (error) => {
        console.error('Error fetching server settings:', error);
      }
    );

    const unsubscribeAdmin = adminDocRef.onSnapshot(
      (doc) => {
        if (doc.exists) {
          isAdminOnline.value = doc.data().online || false;
        } else {
          console.warn('Admin document does not exist');
        }
      },
      (error) => {
        console.error('Error fetching admin online status:', error);
      }
    );

    const unsubscribeRewards = mysteriousBoxRef.onSnapshot(
      (doc) => {
        if (doc.exists && doc.data().rewards) {
          rewards.value = doc.data().rewards.length > 0 ? doc.data().rewards : [{ itemName: '', chance: 0 }];
        } else {
          rewards.value = [{ itemName: '', chance: 0 }];
        }
      },
      (error) => {
        console.error('Error fetching mysterious box rewards:', error);
      }
    );

    const unsubscribeWeeklyBuilding1 = weeklyRewardsBuilding1Ref.onSnapshot(
      (doc) => {
        if (doc.exists && doc.data().tiers) {
          weeklyTiersBuilding1.value = doc.data().tiers.length > 0 ? doc.data().tiers : defaultTiers();
        } else {
          weeklyTiersBuilding1.value = defaultTiers();
        }
      },
      (error) => {
        console.error('Error fetching weekly reward tiers building 1:', error);
      }
    );

    const unsubscribeWeeklyBuilding2 = weeklyRewardsBuilding2Ref.onSnapshot(
      (doc) => {
        if (doc.exists && doc.data().tiers) {
          weeklyTiersBuilding2.value = doc.data().tiers.length > 0 ? doc.data().tiers : defaultTiers();
        } else {
          weeklyTiersBuilding2.value = defaultTiers();
        }
      },
      (error) => {
        console.error('Error fetching weekly reward tiers building 2:', error);
      }
    );

    const unsubscribeWeeklyOccupation = weeklyRewardsOccupationRef.onSnapshot(
      (doc) => {
        if (doc.exists && doc.data().tiers) {
          weeklyTiersOccupation.value = doc.data().tiers.length > 0 ? doc.data().tiers : defaultTiers();
        } else {
          weeklyTiersOccupation.value = defaultTiers();
        }
      },
      (error) => {
        console.error('Error fetching weekly reward tiers occupation:', error);
      }
    );

    const unsubscribeBannedItems = bannedItemsRef.onSnapshot(
      (doc) => {
        if (doc.exists && doc.data().bannedItems) {
          bannedItems.value = doc.data().bannedItems;
        } else {
          bannedItems.value = [];
        }
      },
      (error) => {
        console.error('Error fetching banned sale items:', error);
      }
    );

    const unsubscribeBonus = bonusRef.onSnapshot(
      (doc) => {
        if (doc.exists) {
          const data = doc.data();
          bonusPercentage.value = data.bonusPercentage || 0;
          bonusEndDate.value = data.bonusEndTime ? new Date(data.bonusEndTime.seconds * 1000).toISOString().slice(0, 16) : '';
          bonusMessage.value = data.bonusMessage || '';
        } else {
          bonusPercentage.value = 0;
          bonusEndDate.value = '';
          bonusMessage.value = '';
        }
      },
      (error) => {
        console.error('Error fetching bonus settings:', error);
      }
    );

    const unsubscribeBannedChatUsers = bannedChatUsersRef.onSnapshot(
      (doc) => {
        if (doc.exists && doc.data().uids) {
          bannedChatUsers.value = doc.data().uids;
        } else {
          bannedChatUsers.value = [];
        }
      },
      (error) => {
        console.error('Error fetching banned chat users:', error);
      }
    );

    const defaultTiers = () => [
      { min: 1, max: 1, itemName: 'เพชร', quantity: 100 },
      { min: 2, max: 5, itemName: 'เพชร', quantity: 70 },
      { min: 6, max: 10, itemName: 'เพชร', quantity: 60 },
      { min: 11, max: 30, itemName: 'เพชร', quantity: 50 },
      { min: 31, max: 50, itemName: 'เพชร', quantity: 40 },
      { min: 51, max: 70, itemName: 'เพชร', quantity: 30 },
      { min: 71, max: 100, itemName: 'เพชร', quantity: 20 },
    ];

    onUnmounted(() => {
      unsubscribeSettings();
      unsubscribeAdmin();
      unsubscribeRewards();
      unsubscribeWeeklyBuilding1();
      unsubscribeWeeklyBuilding2();
      unsubscribeWeeklyOccupation();
      unsubscribeBannedItems();
      unsubscribeBonus();
      unsubscribeBannedChatUsers();
    });

    onMounted(() => {
      // Load initial bonus data if needed
    });

    const updateMaxPlayer = async () => {
      if (maxPlayer.value < 0 || !Number.isInteger(maxPlayer.value)) {
        maxMessage.value = 'จำนวนผู้เล่นสูงสุดต้องเป็นจำนวนเต็มที่ไม่ติดลบ';
        maxMessageType.value = 'error';
        return;
      }
      isUpdatingMax.value = true;
      maxMessage.value = '';
      maxMessageType.value = '';
      try {
        await serverDocRef.update({ maxPlayer: maxPlayer.value });
        maxMessage.value = 'อัปเดตจำนวนผู้เล่นสูงสุดสำเร็จ';
        maxMessageType.value = 'success';
      } catch (error) {
        console.error('Error updating maxPlayer:', error);
        maxMessage.value = `เกิดข้อผิดพลาดในการอัปเดตจำนวนผู้เล่นสูงสุด: ${error.message}`;
        maxMessageType.value = 'error';
      } finally {
        isUpdatingMax.value = false;
      }
    };

    const toggleCharacterCreation = async () => {
      isTogglingCharacterCreation.value = true;
      toggleMessage.value = '';
      toggleMessageType.value = '';
      try {
        const newValue = !canCreateCharacter.value;
        await serverDocRef.update({ canCreateCharacter: newValue });
        toggleMessage.value = `การสร้างตัวละครถูก${newValue ? 'เปิด' : 'ปิด'}สำเร็จ`;
        toggleMessageType.value = 'success';
      } catch (error) {
        console.error('Error toggling canCreateCharacter:', error);
        toggleMessage.value = `เกิดข้อผิดพลาดในการอัปเดตการตั้งค่าการสร้างตัวละคร: ${error.message}`;
        toggleMessageType.value = 'error';
      } finally {
        isTogglingCharacterCreation.value = false;
      }
    };

    const togglePvp = async () => {
      isTogglingPvp.value = true;
      pvpMessage.value = '';
      pvpMessageType.value = '';
      try {
        const newValue = !isPvpEnabled.value;
        await serverDocRef.update({ pvp: newValue });
        pvpMessage.value = `การต่อสู้ PvP ถูก${newValue ? 'เปิด' : 'ปิด'}สำเร็จ`;
        pvpMessageType.value = 'success';
      } catch (error) {
        console.error('Error toggling PvP:', error);
        pvpMessage.value = `เกิดข้อผิดพลาดในการอัปเดตการตั้งค่า PvP: ${error.message}`;
        pvpMessageType.value = 'error';
      } finally {
        isTogglingPvp.value = false;
      }
    };

    const toggleAdminOnline = async () => {
      isTogglingAdminOnline.value = true;
      adminOnlineMessage.value = '';
      adminOnlineMessageType.value = '';
      try {
        const newValue = !isAdminOnline.value;
        await adminDocRef.update({ online: newValue });
        adminOnlineMessage.value = `สถานะแอดมินถูกตั้งเป็น${newValue ? 'ออนไลน์' : 'ออฟไลน์'}สำเร็จ`;
        adminOnlineMessageType.value = 'success';
      } catch (error) {
        console.error('Error toggling admin online status:', error);
        adminOnlineMessage.value = `เกิดข้อผิดพลาดในการอัปเดตสถานะแอดมิน: ${error.message}`;
        adminOnlineMessageType.value = 'error';
      } finally {
        isTogglingAdminOnline.value = false;
      }
    };

    const toggleAutoApproveTopup = async () => {
      isTogglingAutoApproveTopup.value = true;
      autoApproveMessage.value = '';
      autoApproveMessageType.value = '';
      try {
        const newValue = !isAutoApproveTopup.value;
        await serverDocRef.update({ autoApproveTopup: newValue });
        autoApproveMessage.value = `การอนุมัติเติมเงินอัตโนมัติถูก${newValue ? 'เปิด' : 'ปิด'}สำเร็จ`;
        autoApproveMessageType.value = 'success';
      } catch (error) {
        console.error('Error toggling auto approve topup:', error);
        autoApproveMessage.value = `เกิดข้อผิดพลาดในการอัปเดตการอนุมัติเติมเงินอัตโนมัติ: ${error.message}`;
        autoApproveMessageType.value = 'error';
      } finally {
        isTogglingAutoApproveTopup.value = false;
      }
    };

    const spawnItemToBag = async () => {
      if (!spawnUid.value || !spawnItemName.value) {
        spawnMessage.value = 'กรุณากรอก UID และชื่อไอเทมให้ครบ';
        spawnMessageType.value = 'error';
        return;
      }
      isSpawningItem.value = true;
      spawnMessage.value = '';
      spawnMessageType.value = '';
      try {
        const gameAction = projectFunctions.httpsCallable('gameActions');
        await gameAction({
          action: 'spawnItem',
          targetUid: spawnUid.value,
          templateId: spawnItemName.value
        });
        spawnMessage.value = `เสกไอเทม "${spawnItemName.value}" ให้ผู้เล่น UID ${spawnUid.value} สำเร็จ`;
        spawnMessageType.value = 'success';
        spawnItemName.value = '';
      } catch (error) {
        console.error('Error spawning item:', error);
        spawnMessage.value = `เกิดข้อผิดพลาด: ${error.message}`;
        spawnMessageType.value = 'error';
      } finally {
        isSpawningItem.value = false;
      }
    };

    const addVillagerToCamp = async () => {
      if (!villagerUid.value || !villagerName.value) {
        villagerMessage.value = 'กรุณากรอก UID และชื่อชาวบ้านให้ครบ';
        villagerMessageType.value = 'error';
        return;
      }
      isAddingVillager.value = true;
      villagerMessage.value = '';
      villagerMessageType.value = '';
      const db = projectFirestore;
      try {
        // Find template by name in static JSON
        const templateEntry = Object.entries(gameVillagersData).find(([_, v]) => v.name === villagerName.value);
        if (!templateEntry) {
          throw new Error(`ไม่พบชาวบ้านชื่อ "${villagerName.value}" ใน gameVillagers`);
        }
        const [templateId, gameVillagerData] = templateEntry;
        const newVillagerId = db.collection('villagers').doc().id;
        const newVillagerData = {
          templateId,
          states: {
            level: 1,
            statPoints: 0,
            upgradeStats: { stamina: 0, str: 0, agi: 0, int: 0, luck: 0, defense: 0 },
            createdAt: new Date(),
            lastUsedInAttackAt: null,
            assignedAt: null
          }
        };
        await db.runTransaction(async (transaction) => {
          const campRef = db.collection('camps').doc(villagerUid.value);
          const campDoc = await transaction.get(campRef);
          if (!campDoc.exists) {
            throw new Error('ไม่พบแคมป์ของผู้เล่น');
          }
          const campData = campDoc.data();
          const villagers = campData.villagers || {};
          const capacity = campData.capacity || 0;
          if (Object.keys(villagers).length >= capacity) {
            throw new Error('แคมป์เต็ม ไม่สามารถเพิ่มชาวบ้านได้');
          }
          transaction.update(campRef, {
            [`villagers.${newVillagerId}`]: newVillagerData
          });
          const logRef = db.collection('logs').doc();
          transaction.set(logRef, {
            uid: villagerUid.value,
            action: 'admin_add_villager_to_camp',
            timestamp: new Date(),
            details: {
              villagerId: newVillagerId,
              templateId,
              villagerName: villagerName.value,
              adminAction: true,
              schema: 'villagerV2'
            },
            displayMessage: `แอดมินเพิ่มชาวบ้าน "${villagerName.value}" เข้าแคมป์สำเร็จ`,
            success: true,
            error: null,
            functionName: 'addVillagerToCamp'
          });
        });
        villagerMessage.value = `เพิ่มชาวบ้าน "${villagerName.value}" ให้ผู้เล่น UID ${villagerUid.value} สำเร็จ`;
        villagerMessageType.value = 'success';
        villagerName.value = '';
      } catch (error) {
        console.error('Error adding villager:', error);
        villagerMessage.value = `เกิดข้อผิดพลาด: ${error.message}`;
        villagerMessageType.value = 'error';
        try {
          await db.collection('logs').doc().set({
            uid: villagerUid.value || 'unknown',
            action: 'admin_add_villager_to_camp',
            timestamp: new Date(),
            details: {
              villagerName: villagerName.value || 'ไม่ระบุ',
              errorMessage: error.message,
              adminAction: true
            },
            displayMessage: `ไม่สามารถเพิ่มชาวบ้าน "${villagerName.value}" ได้: ${error.message}`,
            success: false,
            error: error.message,
            functionName: 'addVillagerToCamp'
          });
        } catch (logError) {
          console.error('Failed to log error:', logError);
        }
      } finally {
        isAddingVillager.value = false;
      }
    };

    const addCashToAllPlayers = async () => {
      if (addCashAmount.value <= 0 || !Number.isInteger(addCashAmount.value)) {
        cashMessage.value = 'จำนวนแคชต้องเป็นจำนวนเต็มที่มากกว่า 0';
        cashMessageType.value = 'error';
        return;
      }
      isAddingCash.value = true;
      cashMessage.value = '';
      cashMessageType.value = '';
      try {
        const db = projectFirestore;
        const batch = db.batch();
        const usersSnapshot = await db.collection('users').get();
        usersSnapshot.forEach(userDoc => {
          const currentCash = userDoc.data().cash || 0;
          const newCash = currentCash + addCashAmount.value;
          batch.update(userDoc.ref, { cash: newCash });
          const logRef = db.collection('logs').doc();
          batch.set(logRef, {
            uid: userDoc.id,
            action: 'admin_add_cash',
            timestamp: new Date(),
            details: {
              amount: addCashAmount.value,
              newCash: newCash,
              adminAction: true
            },
            displayMessage: `แอดมินเพิ่มแคช ${addCashAmount.value} ให้ผู้เล่น UID ${userDoc.id}`,
            success: true,
            error: null,
            functionName: 'addCashToAllPlayers'
          });
        });
        await batch.commit();
        cashMessage.value = `เพิ่มแคช ${addCashAmount.value} ให้ผู้เล่นทุกคนสำเร็จ`;
        cashMessageType.value = 'success';
        addCashAmount.value = 0;
      } catch (error) {
        console.error('Error adding cash to all players:', error);
        cashMessage.value = `เกิดข้อผิดพลาดในการเพิ่มแคช: ${error.message}`;
        cashMessageType.value = 'error';
        try {
          await db.collection('logs').doc().set({
            uid: 'all',
            action: 'admin_add_cash',
            timestamp: new Date(),
            details: {
              amount: addCashAmount.value,
              errorMessage: error.message,
              adminAction: true
            },
            displayMessage: `ไม่สามารถเพิ่มแคช ${addCashAmount.value} ได้: ${error.message}`,
            success: false,
            error: error.message,
            functionName: 'addCashToAllPlayers'
          });
        } catch (logError) {
          console.error('Failed to log error:', logError);
        }
      } finally {
        isAddingCash.value = false;
      }
    };

    const addCashToSpecificPlayer = async () => {
      if (!specificUid.value) {
        specificCashMessage.value = 'กรุณากรอก UID ผู้เล่น';
        specificCashMessageType.value = 'error';
        return;
      }
      if (specificCashAmount.value <= 0 || !Number.isInteger(specificCashAmount.value)) {
        specificCashMessage.value = 'จำนวนแคชต้องเป็นจำนวนเต็มที่มากกว่า 0';
        specificCashMessageType.value = 'error';
        return;
      }
      isAddingCashToSpecific.value = true;
      specificCashMessage.value = '';
      specificCashMessageType.value = '';
      try {
        const db = projectFirestore;
        const userRef = db.collection('users').doc(specificUid.value);
        const userDoc = await userRef.get();
        if (!userDoc.exists) {
          throw new Error('ไม่พบผู้เล่นด้วย UID นี้');
        }
        await db.runTransaction(async (transaction) => {
          const userDoc = await transaction.get(userRef);
          if (!userDoc.exists) {
            throw new Error('ไม่พบผู้เล่นด้วย UID นี้');
          }
          const currentCash = userDoc.data().cash || 0;
          const newCash = currentCash + specificCashAmount.value;
          transaction.update(userRef, { cash: newCash });
          const logRef = db.collection('logs').doc();
          transaction.set(logRef, {
            uid: specificUid.value,
            action: 'admin_add_cash_specific',
            timestamp: new Date(),
            details: {
              amount: specificCashAmount.value,
              newCash: newCash,
              adminAction: true
            },
            displayMessage: `แอดมินเพิ่มแคช ${specificCashAmount.value} ให้ผู้เล่น UID ${specificUid.value}`,
            success: true,
            error: null,
            functionName: 'addCashToSpecificPlayer'
          });
        });
        specificCashMessage.value = `เพิ่มแคช ${specificCashAmount.value} ให้ผู้เล่น UID ${specificUid.value} สำเร็จ`;
        specificCashMessageType.value = 'success';
        specificUid.value = '';
        specificCashAmount.value = 0;
      } catch (error) {
        console.error('Error adding cash to specific player:', error);
        specificCashMessage.value = `เกิดข้อผิดพลาด: ${error.message}`;
        specificCashMessageType.value = 'error';
        try {
          await db.collection('logs').doc().set({
            uid: specificUid.value || 'unknown',
            action: 'admin_add_cash_specific',
            timestamp: new Date(),
            details: {
              amount: specificCashAmount.value,
              errorMessage: error.message,
              adminAction: true
            },
            displayMessage: `ไม่สามารถเพิ่มแคช ${specificCashAmount.value} ให้ UID ${specificUid.value} ได้: ${error.message}`,
            success: false,
            error: error.message,
            functionName: 'addCashToSpecificPlayer'
          });
        } catch (logError) {
          console.error('Failed to log error:', logError);
        }
      } finally {
        isAddingCashToSpecific.value = false;
      }
    };

    const closeMysteriousBoxModal = () => {
      showMysteriousBoxModal.value = false;
      rewardMessage.value = '';
      rewardMessageType.value = '';
    };

    const addReward = () => {
      rewards.value.push({ itemName: '', chance: 0 });
    };

    const removeReward = (index) => {
      rewards.value.splice(index, 1);
    };

    const normalizeChances = () => {
      const currentTotal = totalChance.value;
      if (currentTotal === 0) {
        const baseChance = Math.floor(100 / rewards.value.length);
        let remaining = 100 - baseChance * rewards.value.length;
        rewards.value.forEach((reward, index) => {
          reward.chance = baseChance + (index < remaining ? 1 : 0);
        });
      } else {
        const scaleFactor = 100 / currentTotal;
        const scaledChances = rewards.value.map((reward) => (reward.chance || 0) * scaleFactor);
        const flooredChances = scaledChances.map((chance) => Math.floor(chance));
        const totalFloored = flooredChances.reduce((sum, chance) => sum + chance, 0);
        const remaining = 100 - totalFloored;
        const fractionalParts = scaledChances.map((chance, index) => ({
          index,
          fractional: chance - flooredChances[index],
        })).sort((a, b) => b.fractional - a.fractional);
        for (let i = 0; i < remaining; i++) {
          flooredChances[fractionalParts[i].index]++;
        }
        rewards.value.forEach((reward, index) => {
          reward.chance = flooredChances[index];
        });
      }
    };

    const saveRewards = async () => {
      if (totalChance.value > 100) {
        rewardMessage.value = 'โอกาสรวมกันต้องไม่เกิน 100%';
        rewardMessageType.value = 'error';
        return;
      }
      isSavingRewards.value = true;
      rewardMessage.value = '';
      rewardMessageType.value = '';
      try {
        if (rewards.value.some(reward => !reward.itemName)) {
          throw new Error('กรุณากรอกชื่อไอเทมให้ครบทุกช่อง');
        }
        if (rewards.value.some(reward => reward.chance < 0 || reward.chance > 100)) {
          throw new Error('โอกาสของไอเทมต้องอยู่ระหว่าง 0 ถึง 100');
        }
        const filteredRewards = rewards.value.filter(reward => reward.itemName && reward.chance >= 0);
        await mysteriousBoxRef.set({ rewards: filteredRewards }, { merge: true });
        rewardMessage.value = 'บันทึกคลังปริศนาสำเร็จ';
        rewardMessageType.value = 'success';
        showMysteriousBoxModal.value = false;
      } catch (error) {
        console.error('Error saving mysterious box rewards:', error);
        rewardMessage.value = error.message || 'เกิดข้อผิดพลาดในการบันทึกคลังปริศนา';
        rewardMessageType.value = 'error';
      } finally {
        isSavingRewards.value = false;
      }
    };

    const closeWeeklyRewardModalBuilding1 = () => {
      showWeeklyRewardModalBuilding1.value = false;
    };

    const addTierBuilding1 = () => {
      weeklyTiersBuilding1.value.push({ min: 1, max: 1, itemName: '', quantity: 0 });
    };

    const removeTierBuilding1 = (index) => {
      weeklyTiersBuilding1.value.splice(index, 1);
    };

    const saveWeeklyTiersBuilding1 = async () => {
      isSavingWeeklyTiersBuilding1.value = true;
      try {
        for (let tier of weeklyTiersBuilding1.value) {
          if (tier.min > tier.max || tier.quantity <= 0 || !tier.itemName) {
            throw new Error('ข้อมูลช่วงอันดับไม่ถูกต้อง กรุณาตรวจสอบอันดับเริ่มต้น/สิ้นสุด ชื่อไอเทม และจำนวน');
          }
        }
        await weeklyRewardsBuilding1Ref.set({ tiers: weeklyTiersBuilding1.value }, { merge: true });
        showWeeklyRewardModalBuilding1.value = false;
      } catch (error) {
        console.error('Error saving weekly tiers building 1:', error);
      } finally {
        isSavingWeeklyTiersBuilding1.value = false;
      }
    };

    const closeWeeklyRewardModalBuilding2 = () => {
      showWeeklyRewardModalBuilding2.value = false;
    };

    const addTierBuilding2 = () => {
      weeklyTiersBuilding2.value.push({ min: 1, max: 1, itemName: '', quantity: 0 });
    };

    const removeTierBuilding2 = (index) => {
      weeklyTiersBuilding2.value.splice(index, 1);
    };

    const saveWeeklyTiersBuilding2 = async () => {
      isSavingWeeklyTiersBuilding2.value = true;
      try {
        for (let tier of weeklyTiersBuilding2.value) {
          if (tier.min > tier.max || tier.quantity <= 0 || !tier.itemName) {
            throw new Error('ข้อมูลช่วงอันดับไม่ถูกต้อง กรุณาตรวจสอบอันดับเริ่มต้น/สิ้นสุด ชื่อไอเทม และจำนวน');
          }
        }
        await weeklyRewardsBuilding2Ref.set({ tiers: weeklyTiersBuilding2.value }, { merge: true });
        showWeeklyRewardModalBuilding2.value = false;
      } catch (error) {
        console.error('Error saving weekly tiers building 2:', error);
      } finally {
        isSavingWeeklyTiersBuilding2.value = false;
      }
    };

    const closeWeeklyRewardModalOccupation = () => {
      showWeeklyRewardModalOccupation.value = false;
    };

    const addTierOccupation = () => {
      weeklyTiersOccupation.value.push({ min: 1, max: 1, itemName: '', quantity: 0 });
    };

    const removeTierOccupation = (index) => {
      weeklyTiersOccupation.value.splice(index, 1);
    };

    const saveWeeklyTiersOccupation = async () => {
      isSavingWeeklyTiersOccupation.value = true;
      try {
        for (let tier of weeklyTiersOccupation.value) {
          if (tier.min > tier.max || tier.quantity <= 0 || !tier.itemName) {
            throw new Error('ข้อมูลช่วงอันดับไม่ถูกต้อง กรุณาตรวจสอบอันดับเริ่มต้น/สิ้นสุด ชื่อไอเทม และจำนวน');
          }
        }
        await weeklyRewardsOccupationRef.set({ tiers: weeklyTiersOccupation.value }, { merge: true });
        showWeeklyRewardModalOccupation.value = false;
      } catch (error) {
        console.error('Error saving weekly tiers occupation:', error);
      } finally {
        isSavingWeeklyTiersOccupation.value = false;
      }
    };

    const closeBannedItemsModal = () => {
      showBannedItemsModal.value = false;
    };

    const addBannedItem = () => {
      if (newBannedItem.value.trim()) {
        bannedItems.value.push(newBannedItem.value.trim());
        newBannedItem.value = '';
      }
    };

    const removeBannedItem = (index) => {
      bannedItems.value.splice(index, 1);
    };

    const saveBannedItems = async () => {
      isSavingBannedItems.value = true;
      try {
        await bannedItemsRef.set({ bannedItems: bannedItems.value }, { merge: true });
        showBannedItemsModal.value = false;
      } catch (error) {
        console.error('Error saving banned items:', error);
      } finally {
        isSavingBannedItems.value = false;
      }
    };

    const updateAllKillerActive = async () => {
      isUpdatingKiller.value = true;
      killerMessage.value = '';
      killerMessageType.value = '';
      try {
        const snapshot = await projectFirestore.collection('killerRobots').get();
        if (snapshot.empty) {
          killerMessage.value = 'ไม่พบเอกสารใน killerRobots';
          killerMessageType.value = 'error';
          return;
        }
        const batch = projectFirestore.batch();
        snapshot.forEach((doc) => {
          batch.update(doc.ref, { active: newKillerActive.value });
        });
        await batch.commit();
        killerMessage.value = `อัปเดต active เป็น ${newKillerActive.value} สำหรับหุ่นยนต์สังหารทั้งหมด (${snapshot.size} ตัว) สำเร็จ`;
        killerMessageType.value = 'success';
      } catch (error) {
        console.error('Error updating killer robots active:', error);
        killerMessage.value = `เกิดข้อผิดพลาด: ${error.message}`;
        killerMessageType.value = 'error';
      } finally {
        isUpdatingKiller.value = false;
      }
    };

    const updateBonus = async () => {
      if (bonusPercentage.value < 0 || bonusPercentage.value > 100) {
        bonusMessageStatus.value = 'เปอร์เซ็นต์โบนัสต้องอยู่ระหว่าง 0-100';
        bonusMessageType.value = 'error';
        return;
      }
      if (!bonusEndDate.value) {
        bonusMessageStatus.value = 'กรุณากำหนดเวลาหมดอายุ';
        bonusMessageType.value = 'error';
        return;
      }
      isUpdatingBonus.value = true;
      bonusMessageStatus.value = '';
      bonusMessageType.value = '';
      try {
        const endTime = new Date(new Date(bonusEndDate.value));
        await bonusRef.set({
          bonusPercentage: bonusPercentage.value,
          bonusEndTime: endTime,
          bonusMessage: bonusMessage.value,
        }, { merge: true });
        bonusMessageStatus.value = 'อัปเดตโบนัสเติมเงินสำเร็จ';
        bonusMessageType.value = 'success';
      } catch (error) {
        console.error('Error updating bonus:', error);
        bonusMessageStatus.value = `เกิดข้อผิดพลาด: ${error.message}`;
        bonusMessageType.value = 'error';
      } finally {
        isUpdatingBonus.value = false;
      }
    };

    const addBannedChatUser = () => {
      if (newBannedChatUser.value.trim()) {
        if (!bannedChatUsers.value.includes(newBannedChatUser.value.trim())) {
          bannedChatUsers.value.push(newBannedChatUser.value.trim());
          newBannedChatUser.value = '';
        } else {
          bannedChatUsersMessage.value = 'UID นี้อยู่ในรายการแล้ว';
          bannedChatUsersMessageType.value = 'error';
        }
      }
    };

    const removeBannedChatUser = (index) => {
      bannedChatUsers.value.splice(index, 1);
    };

    const saveBannedChatUsers = async () => {
      isSavingBannedChatUsers.value = true;
      bannedChatUsersMessage.value = '';
      bannedChatUsersMessageType.value = '';
      try {
        await bannedChatUsersRef.set({ uids: bannedChatUsers.value }, { merge: true });
        bannedChatUsersMessage.value = 'บันทึกรายการผู้เล่นที่ห้ามแชทสำเร็จ';
        bannedChatUsersMessageType.value = 'success';
      } catch (error) {
        console.error('Error saving banned chat users:', error);
        bannedChatUsersMessage.value = `เกิดข้อผิดพลาด: ${error.message}`;
        bannedChatUsersMessageType.value = 'error';
      } finally {
        isSavingBannedChatUsers.value = false;
      }
    };

    return {
      activePlayer,
      maxPlayer,
      isUpdatingMax,
      maxMessage,
      maxMessageType,
      canCreateCharacter,
      isTogglingCharacterCreation,
      toggleMessage,
      toggleMessageType,
      showMysteriousBoxModal,
      rewards,
      isSavingRewards,
      rewardMessage,
      rewardMessageType,
      spawnUid,
      spawnItemName,
      isSpawningItem,
      spawnMessage,
      spawnMessageType,
      addCashAmount,
      isAddingCash,
      cashMessage,
      cashMessageType,
      specificUid,
      specificCashAmount,
      isAddingCashToSpecific,
      specificCashMessage,
      specificCashMessageType,
      isPvpEnabled,
      isTogglingPvp,
      pvpMessage,
      pvpMessageType,
      isAdminOnline,
      isTogglingAdminOnline,
      adminOnlineMessage,
      adminOnlineMessageType,
      showWeeklyRewardModalBuilding1,
      weeklyTiersBuilding1,
      isSavingWeeklyTiersBuilding1,
      showWeeklyRewardModalBuilding2,
      weeklyTiersBuilding2,
      isSavingWeeklyTiersBuilding2,
      showWeeklyRewardModalOccupation,
      weeklyTiersOccupation,
      isSavingWeeklyTiersOccupation,
      showBannedItemsModal,
      bannedItems,
      newBannedItem,
      isSavingBannedItems,
      newKillerActive,
      isUpdatingKiller,
      killerMessage,
      killerMessageType,
      bonusPercentage,
      bonusEndDate,
      bonusMessage,
      isUpdatingBonus,
      bonusMessageStatus,
      bonusMessageType,
      isAutoApproveTopup,
      isTogglingAutoApproveTopup,
      autoApproveMessage,
      autoApproveMessageType,
      bannedChatUsers,
      newBannedChatUser,
      isSavingBannedChatUsers,
      bannedChatUsersMessage,
      bannedChatUsersMessageType,
      villagerUid,
      villagerName,
      isAddingVillager,
      villagerMessage,
      villagerMessageType,
      showNpcReclaimModal,
      showUnlockFourDiceModal,
      showUnlockFiveDiceModal,
      showUnlockSixDiceModal,
      totalChance,
      remainingChance,
      updateMaxPlayer,
      toggleCharacterCreation,
      togglePvp,
      toggleAdminOnline,
      spawnItemToBag,
      addVillagerToCamp,
      addCashToAllPlayers,
      addCashToSpecificPlayer,
      closeMysteriousBoxModal,
      addReward,
      removeReward,
      normalizeChances,
      saveRewards,
      closeWeeklyRewardModalBuilding1,
      addTierBuilding1,
      removeTierBuilding1,
      saveWeeklyTiersBuilding1,
      closeWeeklyRewardModalBuilding2,
      addTierBuilding2,
      removeTierBuilding2,
      saveWeeklyTiersBuilding2,
      closeWeeklyRewardModalOccupation,
      addTierOccupation,
      removeTierOccupation,
      saveWeeklyTiersOccupation,
      closeBannedItemsModal,
      addBannedItem,
      removeBannedItem,
      saveBannedItems,
      updateAllKillerActive,
      updateBonus,
      toggleAutoApproveTopup,
      addBannedChatUser,
      removeBannedChatUser,
      saveBannedChatUsers,
    };
  }
};
</script>

<style scoped>
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fmanagerroom.png?alt=media&token=6b9348b6-9364-4ce5-bf58-6e7b9c5b12f1') no-repeat center center fixed,
              linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
  background-size: cover;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.animate-fade-in {
  animation: fadeIn 0.3s ease-in;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.scroll-smooth {
  scroll-behavior: smooth;
}
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: rgba(147, 51, 234, 0.5) rgba(31, 41, 55, 0.5);
}
.scrollbar-thin::-webkit-scrollbar {
  width: 8px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: rgba(31, 41, 55, 0.5);
  border-radius: 4px;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: rgba(147, 51, 234, 0.5);
  border-radius: 4px;
  border: 2px solid rgba(31, 41, 55, 0.5);
}
.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: rgba(147, 51, 234, 0.8);
}
</style>
